import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Star, Music, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { useGame } from "@/context/GameContext";
import { t } from "@/lib/translations";
import { api } from "@/lib/api";
import { LAYERS } from "@/lib/layers";
import ResearchTab from "@/components/ResearchTab";
import ScienceTabContent from "@/components/ScienceTabContent";
import ProgressReport from "@/components/ProgressReport";
import AdBanner from "@/components/AdBanner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function ParentDashboard() {
  const navigate = useNavigate();
  const {
    language,
    soundOn,
    musicOn,
    parentId,
    activeChildId,
    loaded,
    update,
    toggleLanguage,
    toggleSound,
    toggleMusic,
    hasFullAccess,
    email,
    logoutParent,
    isInvestor,
    parentToken,
  } = useGame();

  const [children, setChildren] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAge, setNewAge] = useState("3");
  const [loading, setLoading] = useState(false);
  
  // Cashflow States
  const [cashflowData, setCashflowData] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(false);
  const [txTypeFilter, setTxTypeFilter] = useState("all");
  const [txCatFilter, setTxCatFilter] = useState("all");
  
  // Messaging States
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Change Password States
  const [changePassOpen, setChangePassOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passLoading, setPassLoading] = useState(false);

  // Ensure parent exists — handled by OnboardingWizard now.
  useEffect(() => {
    if (loaded && !parentId) {
      navigate("/");
    }
  }, [loaded, parentId, navigate]);

  // Load messages from backend
  useEffect(() => {
    if (!parentId) return;
    const loadMessages = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${baseUrl}/api/parents/${parentId}/messages`);
        if (res.ok) {
          const list = await res.json();
          setMessages(list);
          setUnreadCount(list.filter((m) => !m.is_read).length);
        }
      } catch (err) {
        console.warn("Gagal memuat pesan:", err);
      }
    };
    loadMessages();
    const interval = setInterval(loadMessages, 25000);
    return () => clearInterval(interval);
  }, [parentId]);

  // Load children — wait for hydration too
  useEffect(() => {
    if (!loaded || !parentId) return;
    const load = async () => {
      try {
        const list = await api.listChildren(parentId);
        setChildren(list);
        if (list.length && !selectedChild) {
          setSelectedChild(list[0]);
          update({ activeChildId: list[0].id });
        }
      } catch (e) {
        toast.error("Gagal memuat anak");
      }
    };
    load();
  }, [loaded, parentId, selectedChild, update]);

  // Load progress for selected child
  useEffect(() => {
    if (!selectedChild) {
      setProgress(null);
      return;
    }
    api.getProgressSummary(selectedChild.id).then(setProgress).catch(() => {});
  }, [selectedChild]);

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast.error(language === "id" ? "Nama wajib diisi" : "Name required");
      return;
    }
    setLoading(true);
    try {
      const c = await api.createChild({
        parent_id: parentId,
        name: newName.trim(),
        age: parseInt(newAge, 10),
      });
      setChildren((cs) => [...cs, c]);
      setSelectedChild(c);
      update({ activeChildId: c.id });
      toast.success(language === "id" ? "Anak ditambahkan!" : "Child added!");
      setAddOpen(false);
      setNewName("");
      setNewAge("3");
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail;
      if (msg === "Child profile already exists" || msg === "Profil anak dengan nama dan usia ini sudah terdaftar.") {
        toast.error(language === "id" ? "Profil anak dengan nama dan usia tersebut sudah terdaftar." : "A child profile with this name and age already exists.");
      } else {
        toast.error(language === "id" ? "Gagal menambahkan profil anak." : "Failed to add child profile.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteChild(id);
      setChildren((cs) => cs.filter((c) => c.id !== id));
      if (selectedChild?.id === id) {
        setSelectedChild(null);
        update({ activeChildId: null });
      }
      toast.success(language === "id" ? "Dihapus" : "Deleted");
    } catch (e) {
      toast.error("Error");
    }
  };

  const startPlay = (child) => {
    update({ activeChildId: child.id });
    navigate(`/play/${child.age}`);
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(language === "id" ? "Mohon isi semua field!" : "Please fill in all fields!");
      return;
    }
    if (newPassword.length < 6) {
      toast.error(language === "id" ? "Password baru minimal 6 karakter." : "New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(language === "id" ? "Konfirmasi password baru tidak cocok!" : "New password confirmation does not match!");
      return;
    }

    setPassLoading(true);
    try {
      await api.changePassword(parentId, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(language === "id" ? "Password berhasil diubah!" : "Password changed successfully!");
      setChangePassOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail;
      if (msg === "Current password incorrect") {
        toast.error(language === "id" ? "Password saat ini salah." : "Current password incorrect.");
      } else {
        toast.error(language === "id" ? "Gagal mengubah password." : "Failed to change password.");
      }
    } finally {
      setPassLoading(false);
    }
  };

  const fetchCashflow = useCallback(async () => {
    if (!isInvestor || !parentToken) return;
    setCashflowLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/financial/cashflow`, {
        headers: {
          Authorization: `Bearer ${parentToken}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setCashflowData(data);
      }
    } catch (err) {
      console.error("Gagal mengambil data cashflow:", err);
    } finally {
      setCashflowLoading(false);
    }
  }, [isInvestor, parentToken]);

  useEffect(() => {
    if (isInvestor) {
      fetchCashflow();
    }
  }, [isInvestor, fetchCashflow]);

  const handleDownloadCSV = async () => {
    if (!parentToken) return;
    try {
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/financial/cashflow/download`, {
        headers: {
          Authorization: `Bearer ${parentToken}`
        }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `laporan_arus_kas_babylabs_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        toast.error("Gagal mengunduh laporan keuangan.");
      }
    } catch (err) {
      toast.error("Terjadi kesalahan saat mengunduh laporan.");
    }
  };

  return (
    <div
      className="min-h-screen p-4 sm:p-8"
      style={{
        background:
          "linear-gradient(180deg, #FDFBF7 0%, #FEF6E4 100%)",
      }}
      data-testid="parent-dashboard"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/")}
            className="toy-btn w-12 h-12 rounded-full bg-white shadow-[0_4px_0_rgba(0,0,0,0.15)] flex items-center justify-center"
            data-testid="pd-back"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" strokeWidth={3} />
          </button>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-800">
            🛡️ {t("pd_title", language)}
          </h1>
          <div className="w-12" />
        </div>

        {/* Curated Ad Banner Campaign Placement */}
        <div className="mb-8">
          <AdBanner placement="parent_corner_header" />
        </div>

        <Tabs defaultValue="children" className="w-full">
          <TabsList className="flex items-center justify-start w-full max-w-4xl mx-auto rounded-2xl bg-white shadow-md p-1 mb-6 overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-none gap-1 h-auto">
            <TabsTrigger value="children" className="rounded-xl font-display font-bold text-[10px] sm:text-sm py-2 flex-shrink-0" data-testid="tab-children">
              👶 {t("pd_children", language)}
            </TabsTrigger>
            <TabsTrigger value="progress" className="rounded-xl font-display font-bold text-[10px] sm:text-sm py-2 flex-shrink-0" data-testid="tab-progress">
              📊 {t("pd_progress", language)}
            </TabsTrigger>
            <TabsTrigger value="research" className="rounded-xl font-display font-bold text-[10px] sm:text-sm py-2 flex-shrink-0" data-testid="tab-research">
              🧪 {language === "id" ? "Penelitian" : "Research"}
            </TabsTrigger>
            <TabsTrigger value="science" className="rounded-xl font-display font-bold text-[10px] sm:text-sm py-2 flex-shrink-0" data-testid="tab-science">
              📚 {language === "id" ? "Ilmiah" : "Science"}
            </TabsTrigger>
            <TabsTrigger value="info" className="rounded-xl font-display font-bold text-[10px] sm:text-sm py-2 flex-shrink-0" data-testid="tab-info">
              ℹ️ {language === "id" ? "Info" : "Info"}
            </TabsTrigger>
            <TabsTrigger value="inbox" className="rounded-xl font-display font-bold text-[10px] sm:text-sm py-2 relative flex-shrink-0" data-testid="tab-inbox">
              ✉️ {language === "id" ? "Pesan" : "Inbox"}
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4.5 h-4.5 bg-rose-500 text-white rounded-full flex items-center justify-center text-[9px] font-extrabold shadow-sm border border-white px-1">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            {isInvestor && (
              <TabsTrigger value="cashflow" className="rounded-xl font-display font-bold text-[10px] sm:text-sm py-2 flex-shrink-0" data-testid="tab-cashflow">
                📈 {language === "id" ? "Transparansi Kas" : "Cashflow"}
              </TabsTrigger>
            )}
            <TabsTrigger value="settings" className="rounded-xl font-display font-bold text-[10px] sm:text-sm py-2 flex-shrink-0" data-testid="tab-settings">
              ⚙️ {t("pd_settings", language)}
            </TabsTrigger>
          </TabsList>

          {/* Children Tab */}
          <TabsContent value="children" className="space-y-4">
            <div className="flex justify-end mb-4">
              {children.length < 5 ? (
                <Button
                  onClick={() => setAddOpen(true)}
                  className="rounded-2xl bg-sky-500 hover:bg-sky-600 text-white font-bold shadow-[0_4px_0_#0284c7]"
                  data-testid="add-child-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> {t("pd_add_child", language)}
                </Button>
              ) : (
                <div className="text-xs sm:text-sm font-bold text-slate-500 bg-slate-100 px-4 py-2 rounded-xl flex items-center shadow-sm">
                  ⚠️ {language === "id" ? "Batas maksimal 5 profil anak telah tercapai." : "Maximum limit of 5 child profiles reached."}
                </div>
              )}
            </div>
            {children.length === 0 ? (
              <Card className="rounded-3xl border-2 border-dashed bg-white/70">
                <CardContent className="p-10 text-center text-slate-500 font-bold">
                  {t("pd_no_children", language)}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {children.map((c) => {
                  const layer = LAYERS[c.age];
                  return (
                    <Card
                      key={c.id}
                      className="rounded-3xl border-4 overflow-hidden bg-white"
                      style={{ borderColor: layer.color }}
                      data-testid={`child-card-${c.id}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="font-display text-xl">{c.name}</CardTitle>
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center"
                            data-testid={`delete-child-${c.id}`}
                            aria-label="delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-bold text-slate-600">
                            {c.age} {c.age === 1 ? t("year", language) : t("years", language)} ·{" "}
                            {t(layer.themeKey, language)}
                          </div>
                          <div className="flex items-center gap-1 text-amber-500 font-bold">
                            <Star className="w-4 h-4 fill-amber-400" />
                            {c.stars}
                          </div>
                        </div>
                        <Button
                          onClick={() => startPlay(c)}
                          className="w-full rounded-2xl text-white font-bold shadow-[0_4px_0_rgba(0,0,0,0.2)]"
                          style={{ background: layer.color }}
                          data-testid={`play-child-${c.id}`}
                        >
                          ▶ {t("start_play", language)}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress" className="space-y-4">
            {children.length === 0 ? (
              <Card className="rounded-3xl bg-white/70">
                <CardContent className="p-10 text-center text-slate-500 font-bold">
                  {t("pd_no_children", language)}
                </CardContent>
              </Card>
            ) : (
              <>
                <Select
                  value={selectedChild?.id || ""}
                  onValueChange={(v) => {
                    const c = children.find((x) => x.id === v);
                    setSelectedChild(c);
                    update({ activeChildId: c?.id });
                  }}
                >
                  <SelectTrigger className="rounded-2xl bg-white max-w-sm" data-testid="progress-select-child">
                    <SelectValue placeholder={t("pd_select_child", language)} />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.age} {c.age === 1 ? t("year", language) : t("years", language)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <ProgressReport child={selectedChild} language={language} />
              </>
            )}
          </TabsContent>


          {/* Research Tab */}
          <TabsContent value="research">
            <ResearchTab profiles={children} />
          </TabsContent>

          {/* Scientific Basis Tab */}
          <TabsContent value="science">
            <ScienceTabContent />
          </TabsContent>

          {/* Info & Panduan Tab */}
          <TabsContent value="info" className="space-y-4">
            <Card className="rounded-3xl bg-white shadow-lg border-0 overflow-hidden">
              <CardHeader className="pb-2 bg-gradient-to-r from-sky-50 to-purple-50 p-6">
                <CardTitle className="font-display text-xl text-slate-800 flex items-center gap-2">
                  ℹ️ {language === "id" ? "Panduan & Informasi Dasbor" : "Dashboard Information & Guide"}
                </CardTitle>
                <p className="text-slate-500 text-xs font-bold mt-1 leading-relaxed">
                  {language === "id" 
                    ? "Pelajari fungsi dan kegunaan dari setiap fitur di dalam Pojok Orang Tua untuk mengoptimalkan tumbuh kembang si kecil." 
                    : "Learn about the functions and purposes of each feature within the Parent Corner to optimize your child's developmental journey."}
                </p>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Children Guide */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-2xl bg-sky-100 flex items-center justify-center flex-shrink-0 border-2 border-sky-200">
                      <span className="text-2xl">👶</span>
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-slate-800 text-sm">
                        {language === "id" ? "Profil Anak (Children)" : "Child Profiles"}
                      </h4>
                      <p className="text-slate-500 text-xs font-bold leading-relaxed mt-1">
                        {language === "id"
                          ? "Tempat mengelola profil buah hati Anda (maksimal 5 anak). Anda dapat menyesuaikan nama dan usia anak untuk menyajikan game edukasi yang tepat secara sensori dan kognitif."
                          : "Manage profiles for your children (maximum of 5). Customize names and ages to deliver sensory and cognitively age-appropriate educational games."}
                      </p>
                    </div>
                  </div>

                  {/* Progress Guide */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0 border-2 border-amber-200">
                      <span className="text-2xl">📊</span>
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-slate-800 text-sm">
                        {language === "id" ? "Laporan Perkembangan (Progress)" : "Development Reports"}
                      </h4>
                      <p className="text-slate-500 text-xs font-bold leading-relaxed mt-1">
                        {language === "id"
                          ? "Pantau pencapaian anak secara visual. Termasuk jumlah bintang terkumpul, diagram lingkaran akurasi respon jawaban, dan statistik interaksi per permainan."
                          : "Visually track your child's milestones. Includes total stars collected, response accuracy charts, and interaction statistics per game."}
                      </p>
                    </div>
                  </div>

                  {/* Research Guide */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center flex-shrink-0 border-2 border-indigo-200">
                      <span className="text-2xl">🧪</span>
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-slate-800 text-sm">
                        {language === "id" ? "Analisis Penelitian (Research Mode)" : "Research & Telemetries"}
                      </h4>
                      <p className="text-slate-500 text-xs font-bold leading-relaxed mt-1">
                        {language === "id"
                          ? "Fitur premium untuk merekam respon sensori-motorik halus anak saat bermain secara real-time. Anda dapat mengekspor data komprehensif ini dalam format CSV untuk dianalisis bersama dokter atau psikolog anak."
                          : "A premium feature that captures your child's fine sensory-motor responses in real-time during play. You can export this comprehensive dataset as CSV for consultation with clinicians or pediatric psychologists."}
                      </p>
                    </div>
                  </div>

                  {/* Scientific Guide */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0 border-2 border-emerald-200">
                      <span className="text-2xl">📚</span>
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-slate-800 text-sm">
                        {language === "id" ? "Dasar Ilmiah (Scientific Basis)" : "Scientific Basis"}
                      </h4>
                      <p className="text-slate-500 text-xs font-bold leading-relaxed mt-1">
                        {language === "id"
                          ? "Pelajari kajian literatur akademis, daftar jurnal terkemuka (APA, PubMed, Scopus), dan referensi teoretis yang menjadi dasar perancangan setiap game sensorik dari Layer 1 hingga Layer 5."
                          : "Explore peer-reviewed literature, leading journal citations (APA, PubMed, Scopus), and theoretical frameworks underpinning the design of our sensory games from Layer 1 to Layer 5."}
                      </p>
                    </div>
                  </div>

                  {/* Message Guide */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center flex-shrink-0 border-2 border-rose-200">
                      <span className="text-2xl">✉️</span>
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-slate-800 text-sm">
                        {language === "id" ? "Pesan Masuk (Official Inbox)" : "Official Messages"}
                      </h4>
                      <p className="text-slate-500 text-xs font-bold leading-relaxed mt-1">
                        {language === "id"
                          ? "Pusat informasi interaktif untuk menerima pemberitahuan penting, masukan pengasuhan, panduan mingguan, dan surat kabar resmi langsung dari tim ahli BabyLabs."
                          : "Interactive communication hub to receive critical announcements, developmental parenting tips, weekly milestones, and official newsletters from the BabyLabs expert panel."}
                      </p>
                    </div>
                  </div>

                  {/* Settings Guide */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center flex-shrink-0 border-2 border-slate-200">
                      <span className="text-2xl">⚙️</span>
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-slate-800 text-sm">
                        {language === "id" ? "Pengaturan Akun (Settings)" : "Settings & Audio"}
                      </h4>
                      <p className="text-slate-500 text-xs font-bold leading-relaxed mt-1">
                        {language === "id"
                          ? "Kelola preferensi bahasa aplikasi (Indonesia / Inggris), hidupkan/matikan musik latar dan efek suara, serta sinkronkan paket langganan premium BCA Virtual Account Anda."
                          : "Configure application language (Indonesian / English), toggle background music or sound effects, and synchronize your BCA Virtual Account premium subscription status."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="pt-6 border-t border-slate-100 mt-2">
                  <h4 className="font-display font-bold text-slate-800 text-sm mb-3">
                    {language === "id" 
                      ? "Segala masukan dan saran terkait aplikasi ini bisa hubungi:" 
                      : "For any feedback or suggestions regarding this application, please contact:"}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 text-slate-600">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                        <span className="text-sm font-bold">📱</span>
                      </div>
                      <span className="text-xs font-bold">1. Hp/WA : 081368442442</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-600">
                      <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center text-sky-600">
                        <span className="text-sm font-bold">📧</span>
                      </div>
                      <span className="text-xs font-bold">2. email : neotunas.id@gmail.com</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inbox Tab */}
          <TabsContent value="inbox" className="space-y-4">
            <Card className="rounded-3xl bg-white shadow-md border-0 overflow-hidden">
              <CardContent className="p-6">
                <h3 className="font-display font-bold text-lg mb-4 text-slate-800">
                  {language === "id" ? "Kotak Masuk Orang Tua" : "Parent Inbox"}
                </h3>
                {messages.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 font-bold">
                    {language === "id" ? "Belum ada pesan resmi dari BabyLabs." : "No official messages from BabyLabs yet."}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <button
                        key={msg.id}
                        type="button"
                        onClick={() => {
                          if (!msg.is_read) {
                            msg.is_read = true;
                            setUnreadCount((prev) => Math.max(0, prev - 1));
                          }
                        }}
                        className={`w-full text-left p-4 rounded-2xl border transition-all flex flex-col justify-between ${
                          msg.is_read 
                            ? "bg-slate-50/50 border-slate-100 hover:bg-slate-50" 
                            : "bg-amber-50/20 border-amber-200/50 hover:bg-amber-50/30"
                        }`}
                      >
                        <div className="flex justify-between items-start gap-3 w-full">
                          <div className="flex items-center gap-2">
                            {!msg.is_read && (
                              <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                            )}
                            <h4 className="font-display text-sm font-bold text-slate-800">
                              {msg.title}
                            </h4>
                          </div>
                          <span className="text-[10px] text-slate-400 font-bold">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 font-bold mt-2 leading-relaxed">
                          {msg.content}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="rounded-3xl bg-white">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-bold">{t("pd_lang", language)}</Label>
                  <Button
                    onClick={toggleLanguage}
                    variant="outline"
                    className="rounded-2xl font-bold"
                    data-testid="settings-lang"
                  >
                    {language === "id" ? "Bahasa Indonesia" : "English"}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-base font-bold flex items-center gap-2">
                    <Volume2 className="w-4 h-4" /> {t("pd_sound", language)}
                  </Label>
                  <Switch checked={soundOn} onCheckedChange={toggleSound} data-testid="settings-sound" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-base font-bold flex items-center gap-2">
                    <Music className="w-4 h-4" /> {t("pd_music", language)}
                  </Label>
                  <Switch checked={musicOn} onCheckedChange={toggleMusic} data-testid="settings-music" />
                </div>
                
                {/* Account Actions */}
                <div className="pt-4 border-t border-slate-100">
                  <div className="mb-4 flex justify-between items-end">
                    <div>
                      <Label className="text-base font-bold text-slate-700">Akun (Email)</Label>
                      <div className="text-sm font-bold text-slate-500">{email || "Guest"}</div>
                    </div>
                    {parentId && (
                      <Button
                        onClick={() => setChangePassOpen(true)}
                        variant="outline"
                        className="rounded-xl font-bold text-xs"
                      >
                        🔑 {language === "id" ? "Ganti Password" : "Change Password"}
                      </Button>
                    )}
                  </div>
                  <Button 
                    onClick={() => {
                      logoutParent();
                      navigate("/");
                    }}
                    variant="destructive"
                    className="w-full rounded-2xl font-bold font-display"
                  >
                    {language === "id" ? "Keluar / Logout" : "Logout"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cashflow Tab */}
          {isInvestor && (
            <TabsContent value="cashflow" className="space-y-6">
              {cashflowLoading ? (
                <div className="text-center py-12 font-bold text-slate-500">Memuat Laporan Keuangan...</div>
              ) : !cashflowData ? (
                <div className="text-center py-12 font-bold text-slate-500">Gagal memuat data laporan keuangan.</div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Card className="rounded-3xl border-2 border-slate-100 bg-white shadow-sm overflow-hidden">
                      <div className="p-6 bg-gradient-to-br from-emerald-50 to-white">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Pendapatan</span>
                        <h3 className="font-display text-2xl font-bold text-emerald-600 mt-1">
                          Rp {cashflowData.summary.total_income.toLocaleString("id-ID")}
                        </h3>
                        <p className="text-[11px] font-bold text-slate-500 mt-2">
                          Dari penjualan premium & sponsor iklan
                        </p>
                      </div>
                    </Card>

                    <Card className="rounded-3xl border-2 border-slate-100 bg-white shadow-sm overflow-hidden">
                      <div className="p-6 bg-gradient-to-br from-rose-50 to-white">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Pengeluaran</span>
                        <h3 className="font-display text-2xl font-bold text-rose-600 mt-1">
                          Rp {cashflowData.summary.total_expense.toLocaleString("id-ID")}
                        </h3>
                        <p className="text-[11px] font-bold text-slate-500 mt-2">
                          Operasional, pemasaran & ujrah founder
                        </p>
                      </div>
                    </Card>

                    <Card className="rounded-3xl border-2 border-slate-100 bg-white shadow-sm overflow-hidden">
                      <div className="p-6 bg-gradient-to-br from-sky-50 to-white">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Laba Bersih (Syirkah)</span>
                        <h3 className="font-display text-2xl font-bold text-slate-800 mt-1">
                          Rp {cashflowData.summary.net_profit.toLocaleString("id-ID")}
                        </h3>
                        <p className="text-[11px] font-bold text-slate-500 mt-2">
                          Pendapatan dikurangi pengeluaran
                        </p>
                      </div>
                    </Card>
                  </div>

                  {/* Profit Share Distribution Info */}
                  <Card className="rounded-3xl border-2 border-slate-100 bg-white shadow-sm p-6">
                    <h3 className="font-display font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
                      🤝 Perhitungan Pembagian Nisbah Syirkah
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col justify-between">
                        <div>
                          <div className="text-xs font-bold text-slate-400 uppercase">Founder (Syarik Kerja - 60%)</div>
                          <div className="font-display text-xl font-bold text-slate-700 mt-1">
                            Rp {cashflowData.summary.founder_share.toLocaleString("id-ID")}
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-500 font-bold mt-2">Nisbah founder atas pengelolaan operasional</span>
                      </div>

                      <div className="p-4 rounded-2xl bg-amber-50/30 border border-amber-100 flex flex-col justify-between">
                        <div>
                          <div className="text-xs font-bold text-slate-400 uppercase">Investor (4% per Slot)</div>
                          <div className="font-display text-xl font-bold text-amber-600 mt-1">
                            Rp {cashflowData.summary.investor_total_share.toLocaleString("id-ID")}
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-500 font-bold mt-2">
                          Untuk {cashflowData.summary.active_slots} slot terisi (maks. 40% untuk 10 slot)
                        </span>
                      </div>

                      <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col justify-between">
                        <div>
                          <div className="text-xs font-bold text-slate-400 uppercase">Dana Cadangan (Retained - 10%)</div>
                          <div className="font-display text-xl font-bold text-slate-700 mt-1">
                            Rp {cashflowData.summary.reserve_fund.toLocaleString("id-ID")}
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-500 font-bold mt-2">Disisihkan untuk cadangan pengembangan platform</span>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <div className="text-sm font-bold text-slate-800">
                          Kepemilikan Slot Investasi Anda: <span className="text-sky-600 font-extrabold">{cashflowData.user_slots} Slot</span>
                        </div>
                        <p className="text-xs text-slate-500 font-bold mt-1">
                          Nisbah bagian hasil Anda adalah {cashflowData.user_slots * 4}% dari Laba Bersih
                        </p>
                      </div>
                      <div className="p-4 rounded-2xl bg-sky-50 border border-sky-100 text-center sm:text-right">
                        <div className="text-xs font-bold text-sky-600 uppercase">Bagi Hasil Yang Anda Terima</div>
                        <div className="font-display text-2xl font-bold text-sky-700 mt-1">
                          Rp {cashflowData.user_estimated_share.toLocaleString("id-ID")}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Progress of Investment & Assets */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="rounded-3xl border-2 border-slate-100 bg-white shadow-sm p-6">
                      <h4 className="font-display font-bold text-slate-800 text-sm mb-3">
                        Slots Modal Terkumpul (Maks. Rp 100 Jt)
                      </h4>
                      <div className="flex justify-between text-xs font-bold text-slate-500 mb-2">
                        <span>{cashflowData.summary.active_slots} / 10 Slot Terisi</span>
                        <span>Rp {(cashflowData.summary.active_slots * 10000000).toLocaleString("id-ID")} / Rp 100.000.000</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-3.5 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-sky-400 to-sky-500 h-full rounded-full transition-all duration-500" 
                          style={{ width: `${(cashflowData.summary.active_slots / 10) * 100}%` }}
                        />
                      </div>
                      <div className="mt-4 p-3 rounded-xl bg-slate-50 text-[11px] font-bold text-slate-500 leading-relaxed">
                        💡 Dana dari investor digunakan sebagai modal cadangan operasional platform yang dilock selama 24 bulan sejak akad perjanjian selesai.
                      </div>
                    </Card>

                    <Card className="rounded-3xl border-2 border-slate-100 bg-white shadow-sm p-6 flex flex-col justify-between">
                      <div>
                        <h4 className="font-display font-bold text-slate-800 text-sm mb-1">
                          Nilai Aset Intelektual (Non-Moneter)
                        </h4>
                        <div className="font-display text-2xl font-bold text-slate-700 mt-2">
                          Rp {cashflowData.summary.intellectual_asset_value.toLocaleString("id-ID")}
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 font-bold mt-2 leading-relaxed">
                        Aset intelektual mencakup source code, brand, aset visual 60+ game, arsitektur kurikulum sensori, dan hak cipta platform Petualangan Bayi (tidak dapat dicairkan tetapi dihitung dalam porsi syirkah pendiri).
                      </p>
                    </Card>
                  </div>

                  {/* Registered Investors Transparent List */}
                  <Card className="rounded-3xl border-2 border-slate-100 bg-white shadow-sm p-6">
                    <h3 className="font-display font-bold text-slate-800 text-sm mb-4">
                      👥 Slot Kemitraan (Investor)
                    </h3>
                    <div className="overflow-x-auto whitespace-nowrap">
                      <table className="w-full text-left text-xs font-bold text-slate-600">
                        <thead>
                          <tr className="border-b border-slate-100 text-slate-400 uppercase text-[10px]">
                            <th className="py-2">Nama Investor</th>
                            <th className="py-2">Email</th>
                            <th className="py-2 text-center">Jumlah Slot</th>
                            <th className="py-2 text-right">Nilai Modal Moneter</th>
                            <th className="py-2 text-right">Perkiraan Bagi Hasil</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashflowData.investors
                            .filter((inv) => inv.email === email)
                            .map((inv, idx) => (
                              <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50/50">
                                <td className="py-3 font-display font-bold text-slate-800">{inv.name}</td>
                                <td className="py-3 text-slate-500">{inv.email}</td>
                                <td className="py-3 text-center text-slate-700">{inv.slots} Slot</td>
                                <td className="py-3 text-right text-slate-700">Rp {(inv.slots * 10000000).toLocaleString("id-ID")}</td>
                                <td className="py-3 text-right text-emerald-600">Rp {inv.estimated_earnings.toLocaleString("id-ID")}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  {/* Transaction Logs */}
                  <Card className="rounded-3xl border-2 border-slate-100 bg-white shadow-sm p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-display font-bold text-lg text-slate-800">
                          📜 Riwayat Arus Kas Platform
                        </h3>
                        <p className="text-slate-500 text-xs font-bold mt-1">
                          Log transparan seluruh arus modal masuk, omzet premium, dan biaya operasional
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          onClick={handleDownloadCSV} 
                          className="rounded-2xl bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs px-4 shadow-[0_4px_0_#0284c7]"
                          data-testid="download-csv-btn"
                        >
                          📥 Unduh CSV
                        </Button>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-4 mb-4">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Tipe Transaksi</label>
                        <Select value={txTypeFilter} onValueChange={setTxTypeFilter}>
                          <SelectTrigger className="w-40 rounded-xl bg-slate-50 border-slate-100 text-xs font-bold">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Semua Tipe</SelectItem>
                            <SelectItem value="income">🟢 Pemasukan</SelectItem>
                            <SelectItem value="expense">🔴 Pengeluaran</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Kategori</label>
                        <Select value={txCatFilter} onValueChange={setTxCatFilter}>
                          <SelectTrigger className="w-44 rounded-xl bg-slate-50 border-slate-100 text-xs font-bold">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Semua Kategori</SelectItem>
                            <SelectItem value="capital_injection">Modal Cadangan</SelectItem>
                            <SelectItem value="premium_purchase">Penjualan Premium</SelectItem>
                            <SelectItem value="ad_payment">Sponsor Iklan</SelectItem>
                            <SelectItem value="server_cost">Biaya Server</SelectItem>
                            <SelectItem value="marketing">Biaya Pemasaran</SelectItem>
                            <SelectItem value="ujrah_founder">Ujrah Founder</SelectItem>
                            <SelectItem value="other">Lain-lain</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto whitespace-nowrap">
                      <table className="w-full text-left text-xs font-bold text-slate-600">
                        <thead>
                          <tr className="border-b border-slate-100 text-slate-400 uppercase text-[10px]">
                            <th className="py-2">Tanggal</th>
                            <th className="py-2">Tipe</th>
                            <th className="py-2">Kategori</th>
                            <th className="py-2">Deskripsi</th>
                            <th className="py-2 text-right">Jumlah</th>
                            <th className="py-2">Dicatat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashflowData.transactions
                            .filter(t => txTypeFilter === "all" || t.transaction_type === txTypeFilter)
                            .filter(t => txCatFilter === "all" || t.category === txCatFilter)
                            .map((tx, idx) => {
                              const isIncome = tx.transaction_type === "income";
                              let catLabel = tx.category;
                              if (tx.category === "capital_injection") catLabel = "Modal Cadangan 💰";
                              else if (tx.category === "premium_purchase") catLabel = "Penjualan Premium 👑";
                              else if (tx.category === "ad_payment") catLabel = "Sponsor Iklan 📢";
                              else if (tx.category === "server_cost") catLabel = "Biaya Server 🖥️";
                              else if (tx.category === "marketing") catLabel = "Pemasaran 📣";
                              else if (tx.category === "ujrah_founder") catLabel = "Ujrah Founder 🧑‍💻";
                              else if (tx.category === "other") catLabel = "Lain-lain ⚙️";

                              return (
                                <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50/50">
                                  <td className="py-3 text-slate-400">
                                    {new Date(tx.recorded_at).toLocaleDateString("id-ID")}
                                  </td>
                                  <td className="py-3">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                      isIncome ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
                                    }`}>
                                      {isIncome ? "Pemasukan" : "Pengeluaran"}
                                    </span>
                                  </td>
                                  <td className="py-3 text-slate-700">{catLabel}</td>
                                  <td className="py-3 text-slate-500 font-normal whitespace-pre-wrap max-w-xs">{tx.description}</td>
                                  <td className={`py-3 text-right font-display font-bold ${
                                    isIncome ? "text-emerald-600" : "text-rose-600"
                                  }`}>
                                    {isIncome ? "+" : "-"} Rp {tx.amount.toLocaleString("id-ID")}
                                  </td>
                                  <td className="py-3 text-slate-400 font-normal uppercase text-[10px]">{tx.recorded_by}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Add child dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="rounded-3xl max-w-md" data-testid="add-child-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {t("pd_add_child", language)}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="child-name" className="font-bold">
                  {t("pd_name", language)}
                </Label>
                <Input
                  id="child-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t("enter_name", language)}
                  className="rounded-2xl mt-1"
                  data-testid="input-child-name"
                />
              </div>
              <div>
                <Label className="font-bold">{t("pd_age", language)}</Label>
                <Select value={newAge} onValueChange={setNewAge}>
                  <SelectTrigger className="rounded-2xl mt-1" data-testid="select-child-age">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((a) => (
                      <SelectItem key={a} value={String(a)}>
                        {a} {a === 1 ? t("year", language) : t("years", language)} —{" "}
                        {t(LAYERS[a].themeKey, language)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-2xl">
                {t("pd_cancel", language)}
              </Button>
              <Button
                onClick={handleAdd}
                disabled={loading}
                className="rounded-2xl bg-sky-500 hover:bg-sky-600 text-white"
                data-testid="confirm-add-child"
              >
                {t("pd_save", language)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change password dialog */}
        <Dialog open={changePassOpen} onOpenChange={setChangePassOpen}>
          <DialogContent className="rounded-3xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                🔑 {language === "id" ? "Ganti Password" : "Change Password"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="current-password-input" className="font-bold">
                  {language === "id" ? "Password Saat Ini" : "Current Password"}
                </Label>
                <Input
                  id="current-password-input"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-2xl mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new-password-input" className="font-bold">
                  {language === "id" ? "Password Baru" : "New Password"}
                </Label>
                <Input
                  id="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-2xl mt-1"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password-input" className="font-bold">
                  {language === "id" ? "Konfirmasi Password Baru" : "Confirm New Password"}
                </Label>
                <Input
                  id="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-2xl mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setChangePassOpen(false)} className="rounded-2xl">
                {t("pd_cancel", language)}
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={passLoading}
                className="rounded-2xl bg-sky-500 hover:bg-sky-600 text-white"
              >
                {passLoading ? "..." : (language === "id" ? "Simpan" : "Save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
